/**
 * NotificationItemContainer
 *
 * The unread/report background + padding wrapper around a single notification
 * row. Ports the wrapper `Container` in
 * `lib/presentation/screens/messages/widgets/notification_item.dart:418-440`:
 *   - background: read → transparent;
 *               else reportContent/reportUser → activeRed;
 *               else → unread highlight (web: `notificationUnread`, set to the
 *               theme `secondary` value per user review 3).
 *   - padding: EdgeInsets.only(left:16, right:16, top:8, bottom:8) when used.
 *
 * The page (Task 15) supplies the already-routed row as `children` (Task 12's
 * router). This component only owns the background + padding chrome and the QA
 * tag, so the per-type item components stay purely presentational.
 */
import React from 'react';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface NotificationItemContainerProps {
  notification: AppNotificationEntity;
  children: React.ReactNode;
}

export const NotificationItemContainer: React.FC<NotificationItemContainerProps> = ({
  notification,
  children,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const { info } = notification;
  const type = info.notificationType as AppNotificationType;
  const isReport =
    type === AppNotificationType.reportContent || type === AppNotificationType.reportUser;

  const backgroundColor = info.read
    ? theme.transparent
    : isReport
      ? theme.activeRed
      : theme.notificationUnread;

  return (
    <div
      {...qaAttr({
        id: `inbox-notification-${notification.id ?? info.timestamp}`,
        value: AppNotificationType[type],
        // Read state is otherwise only conveyed by the background tint, which a
        // UI test can read only through a computed-style probe. `info.read` is
        // optional on the entity (Flutter's `bool read = false` is
        // non-nullable, `lib/data/models/notification_info.dart:31`), so an
        // absent value is 'unread', matching the `info.read` tint predicate
        // above.
        status: info.read ? 'read' : 'unread',
      })}
      style={{
        backgroundColor,
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
      }}
    >
      {children}
    </div>
  );
};
